'use client';

import React, { useMemo, useState } from 'react';
import { Product } from "../components/Product";
import { useCart } from "../data/CartContext";
import { track } from "../lib/track";

type Props = {
  product: Product;
  /** Compact = horizontal controls. Block = stepper + full-width button stacked. InlineCard = stacked card. */
  layout?: 'compact' | 'block' | 'inlineCard';
  /** Use +/− stepper instead of a select. */
  useStepper?: boolean;
  /** Override button label (e.g. “Start Your Detox Ritual”). */
  buttonLabel?: string;
  /** Optional className passthrough for container. */
  className?: string;
  /** Optional callback after add. */
  onAdd?: (qty: number) => void;
};

const clamp = (n: number, min = 1, max = 99) => Math.max(min, Math.min(max, n));

const QtyStepper: React.FC<{ value: number; onChange: (n: number) => void }> = ({ value, onChange }) => (
  <div className="flex items-center border border-[var(--color-accent)] rounded-xl overflow-hidden">
    <button
      type="button"
      aria-label="Decrease quantity"
      className="px-3 py-2 text-gray-600 hover:bg-[var(--color-accent)]/50"
      onClick={() => onChange(clamp(value - 1))}
    >
      −
    </button>
    <span className="px-4 text-gray-900 select-none min-w-[2ch] text-center">{value}</span>
    <button
      type="button"
      aria-label="Increase quantity"
      className="px-3 py-2 text-gray-600 hover:bg-[var(--color-accent)]/50"
      onClick={() => onChange(clamp(value + 1))}
    >
      +
    </button>
  </div>
);

const QtySelect: React.FC<{ value: number; onChange: (n: number) => void }> = ({ value, onChange }) => (
  <>
    <label htmlFor="qty" className="sr-only">Quantity</label>
    <select
      id="qty"
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value, 10))}
      className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:ring-[var(--color-brand)] focus:border-[var(--color-brand)]"
    >
      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
        <option key={n} value={n}>{n}</option>
      ))}
    </select>
  </>
);

const AddToCart: React.FC<Props> = ({
  product,
  layout = 'compact',
  useStepper = true,
  buttonLabel,
  className = '',
  onAdd,
}) => {
  const { addToCart } = useCart();
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);

  const priceNumber = useMemo(
    () => parseFloat(String(product.price).replace(/[^0-9.]/g, '')) || 0,
    [product.price]
  );
  const totalPrice = qty * priceNumber;

  const handleAddToCart = async () => {
    if (busy) return;
    setBusy(true);

    addToCart({ ...product, qty, totalPrice });

    // analytics
    // analytics
track(
  "add_to_cart",
  {
    label: `Added ${qty} × ${product.title} to cart`,
    component: "AddToCart",
  },
  {
    event: "add_to_cart",
    label: `Added ${qty} × ${product.title} to cart`,
    component: "AddToCart",
    meta: {
      productTitle: product.title,
      productPrice: priceNumber,
      quantity: qty,
      totalPrice: totalPrice,
    },
  }
);
track(
  "cta_click",
  { label: `Shop ${product.title} | Add to Cart`, component: "AddToCart" },
  { event: "cta_click", label: `Shop ${product.title} | Add to Cart`, component: "AddToCart", meta: { productTitle: product.title, productPrice: priceNumber, quantity: qty } }
);

    // page-level toast (the page listens to this)
    window.dispatchEvent(new CustomEvent('add-to-cart', { detail: { product, qty } }));
    onAdd?.(qty);

    // brief disabled state to prevent double-taps
    setTimeout(() => setBusy(false), 600);
  };

  const QtyControl = useStepper
    ? <QtyStepper value={qty} onChange={setQty} />
    : <QtySelect value={qty} onChange={setQty} />;

  // Block: stepper compact-left, button flex-1 right (product detail page)
  if (layout === 'block') {
    return (
      <div className={`flex items-center gap-3 ${className}`}>
        {QtyControl}
        <button
          type="button"
          onClick={handleAddToCart}
          disabled={busy}
          className="flex-1 bg-[var(--color-text-brand)] hover:bg-[var(--color-brand-hover)] text-white font-semibold rounded-xl px-5 py-3 transition-all shadow-sm hover:shadow-md disabled:opacity-70 disabled:cursor-not-allowed text-base"
        >
          {buttonLabel ?? (busy ? 'Adding…' : 'Add to Cart')}
        </button>
        <span id="cart-announcer" className="sr-only" aria-live="polite" />
      </div>
    );
  }

  // Compact horizontal (recommended)
  if (layout === 'compact') {
    return (
      <div className={`flex items-center gap-3 ${className}`}>
        {QtyControl}
        <button
          type="button"
          onClick={handleAddToCart}
          disabled={busy}
          className={`bg-[var(--color-text-brand)] hover:bg-[var(--color-brand-hover)] text-white font-medium rounded-xl px-5 py-2.5 transition-all shadow-sm hover:shadow disabled:opacity-70 disabled:cursor-not-allowed`}
        >
          {buttonLabel ?? (busy ? 'Adding…' : 'Add to Cart')}
        </button>
        <span id="cart-announcer" className="sr-only" aria-live="polite" />
      </div>
    );
  }

  // Stacked inline card (if you ever want it inside a bordered area)
  return (
    <div className={`bg-white/80 backdrop-blur-md border border-[var(--color-accent)] rounded-2xl p-4 shadow-sm ${className}`}>
      <div className="flex items-center justify-between mb-3">
        {QtyControl}
      </div>
      <button
        type="button"
        onClick={handleAddToCart}
        disabled={busy}
        className="w-full bg-[var(--color-brand)] hover:bg-[var(--color-brand)] text-white font-medium rounded-xl px-5 py-3 transition-all shadow-sm hover:shadow disabled:opacity-70 disabled:cursor-not-allowed"
      >
        {buttonLabel ?? (busy ? 'Adding…' : 'Add to Cart')}
      </button>
      <span id="cart-announcer" className="sr-only" aria-live="polite" />
    </div>
  );
};

export default AddToCart;
